// src/pages/BookingFlow.jsx
"use client";
import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import clsx from "clsx";
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { useAuth } from "./context/AuthContext";
import { previewOne, previewAll, api } from "../lib/api";
import ServicePickerPremium from "./ServicePickerPremium";
import { API_BASE_URL } from "../config/api.config";
import { NavbarDemo } from "./NavBar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import brewingCoffeeAnimation from "../images/Brewing Coffee.json";
import { trackEvent, trackBookingEvent } from "../utils/analytics";

const API = API_BASE_URL;

/* ---------------- helpers ---------------- */
async function apiFetch(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    credentials: "include",
  });
  if (!res.ok) {
    let data;
    try {
      data = await res.json();
    } catch {
      data = { message: `HTTP ${res.status}` };
    }
    const err = new Error(data?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return res.json();
}
async function fetchTherapistCounts(
  dateISO,
  serviceId,
  clientPostalCode,
  timeExact,
  timePref,
  clientGender,
) {
  const params = new URLSearchParams();
  if (dateISO) {
    params.append("dateISO", dateISO);
  }
  if (serviceId) {
    const serviceIdStr =
      typeof serviceId === "object"
        ? serviceId?._id || serviceId?.id
        : serviceId;
    if (serviceIdStr) {
      params.append("serviceId", String(serviceIdStr));
    }
  }
  if (clientPostalCode) {
    params.append("clientPostalCode", clientPostalCode);
  }
  if (timeExact) {
    params.append("timeExact", timeExact);
  }
  if (timePref) {
    params.append("timePref", timePref);
  }
  if (clientGender) {
    params.append("clientGender", clientGender);
  }
  const url = `${API}/therapists/counts-by-setting?${params.toString()}`;
  return apiFetch(url);
}
function personaIsFilled(p) {
  if (!p) return false;
  const keys = [
    "comfortVibe",
    "idealEscape",
    "resetRitual",
    "therapistEnergy",
    "desiredOutcome",
    "moodColor",
  ];
  const n = keys.reduce((c, k) => (p[k] ? c + 1 : c), 0);
  return n >= 3;
}

function personaIsComplete(p) {
  if (!p) return false;
  const keys = [
    "comfortVibe",
    "idealEscape",
    "resetRitual",
    "therapistEnergy",
    "desiredOutcome",
    "moodColor",
  ];
  // Require all 6 persona fields to be filled
  return keys.every((k) => p[k] != null && p[k] !== "");
}

// Helper function to match search query to service ID
// Note: Disabled for now - services now come from backend with MongoDB IDs
// TODO: Update to match against backend service names/descriptions
function matchSearchQueryToService(query) {
  return null;

  // Commented out old hardcoded matching logic
  // if (!query) return null;
  // const normalizedQuery = query.toLowerCase().trim();
  // const serviceKeywords = {
  //   relaxation: ["relaxation", "relax", "swedish", "gentle", "calm", "soothing"],
  //   deep_tissue: ["deep tissue", "deep", "tissue", "firm", "intense", "knots", "tension"],
  //   sports: ["sports", "athletic", "athlete", "recovery", "performance"],
  //   prenatal: ["prenatal", "pregnancy", "pregnant", "maternity", "expecting"],
  //   thai: ["thai", "stretch", "stretching", "yoga"],
  //   reflexology: ["reflexology", "foot", "feet", "pressure points"],
  // };
  // for (const [serviceId, keywords] of Object.entries(serviceKeywords)) {
  //   if (keywords.some(keyword => normalizedQuery.includes(keyword))) {
  //     return serviceId;
  //   }
  // }
  // return null;
}

/* ---------------- tiny UI atoms ---------------- */
function StepperHeader({ steps, active, onJump }) {
  return (
    <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-sm pb-4 pt-2 mb-6 border-b border-gray-200">
      <div className="flex items-center gap-3">
        {steps.map((s, idx) => {
          const isActive = idx === active;
          const Segment = onJump ? "button" : "div";
          const segmentProps = onJump
            ? { type: "button", onClick: () => onJump(idx) }
            : {};

          return (
            <Segment
              key={s.key}
              {...segmentProps}
              className={clsx(
                "flex-1 rounded-full h-2 overflow-hidden bg-gray-100",
                onJump ? "cursor-pointer" : "cursor-default",
              )}
              aria-label={onJump ? s.label : undefined}>
              <div
                className={clsx(
                  "h-full transition-all",
                  isActive ? "bg-gray-900" : "bg-gray-300",
                )}
                style={{ width: "100%" }}
              />
            </Segment>
          );
        })}
      </div>
      <div className="mt-3 grid grid-cols-3 text-center text-sm font-medium text-gray-700">
        {steps.map((s, idx) => (
          <div
            key={s.key}
            className={clsx(
              idx === active ? "text-gray-900" : "text-gray-500",
            )}>
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function CardOption({ icon, label, selected, onClick, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "w-full text-left rounded-2xl border bg-white transition hover:shadow-sm focus:outline-none",
        "p-4 sm:p-5",
        selected ? "border-gray-900 ring-2 ring-gray-200" : "border-gray-200",
      )}>
      <div className="flex items-center gap-2.5">
        <span className="text-[22px] leading-none">{icon}</span>
        <span className="font-semibold text-gray-900 leading-none">
          {label}
        </span>
      </div>
      {typeof count === "number" && (
        <div className="mt-3 inline-flex text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800">
          {count} therapists available
        </div>
      )}
    </button>
  );
}

function Chip({ children, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "px-3 py-1.5 rounded-full text-sm border transition",
        active
          ? "bg-gray-900 text-white border-gray-900"
          : "bg-white text-gray-700 border-gray-200 hover:border-gray-300",
      )}>
      {children}
    </button>
  );
}

/* ---------------- date helpers ---------------- */
function formatDateInput(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function todayStr() {
  return formatDateInput(new Date());
}
function tomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return formatDateInput(d);
}
function nextWeekendStr() {
  const d = new Date();
  const day = d.getDay();
  const daysUntilSat = (6 - day + 7) % 7 || 7;
  d.setDate(d.getDate() + daysUntilSat);
  return formatDateInput(d);
}

/* ---------------- persona content (cards like screenshot) ---------------- */
const personaPairs = [
  {
    left: {
      qkey: "comfortVibe",
      title: "Comfort energy",
      options: [
        { value: "DogPlayfulGrounding", label: "Loyal & playful", icon: "🐶" },
        { value: "CatCalmSoothing", label: "Calm & soothing", icon: "🐱" },
        { value: "BirdLightUplifting", label: "Light & uplifting", icon: "🐦" },
        {
          value: "PlantSteadyGrounding",
          label: "Steady & grounding",
          icon: "🌱",
        },
        { value: "Flexible", label: "I’m flexible", icon: "🤷" },
      ],
    },
    right: {
      qkey: "idealEscape",
      title: "Ideal escape",
      options: [
        { value: "Nature", label: "Into nature", icon: "🏞️" },
        { value: "Cozy", label: "Cozy corner", icon: "📚" },
        { value: "Creative", label: "Creative space", icon: "🎶" },
        { value: "Social", label: "Out with friends", icon: "🎉" },
        { value: "Rest", label: "Just resting", icon: "🛌" },
      ],
    },
  },
  {
    left: {
      qkey: "resetRitual",
      title: "Reset ritual",
      options: [
        { value: "Water", label: "Water", icon: "🌊" },
        { value: "Warmth", label: "Warmth", icon: "🔥" },
        { value: "Air", label: "Fresh air", icon: "🍃" },
        { value: "Sound", label: "Sound", icon: "🎵" },
        { value: "Silence", label: "Silence", icon: "🌌" },
      ],
    },
    right: {
      qkey: "therapistEnergy",
      title: "Therapist energy",
      options: [
        { value: "Zen", label: "Zen monk", icon: "🌿" },
        { value: "BestFriend", label: "Best friend", icon: "🗣️" },
        { value: "Guide", label: "Intuitive guide", icon: "🔮" },
        { value: "Coach", label: "Motivator coach", icon: "⚡" },
      ],
    },
  },
  {
    left: {
      qkey: "desiredOutcome",
      title: "Desired outcome",
      options: [
        { value: "CloudLight", label: "Light as a cloud", icon: "☁️" },
        { value: "Energized", label: "Energized", icon: "🔋" },
        { value: "Balanced", label: "Balanced", icon: "🧘" },
        { value: "CaredFor", label: "Cared for", icon: "❤️" },
      ],
    },
    right: {
      qkey: "moodColor",
      title: "Mood color",
      options: [
        { value: "Blue", label: "Blue", icon: "🔵" },
        { value: "Yellow", label: "Yellow", icon: "🌕" },
        { value: "Red", label: "Red", icon: "🔴" },
        { value: "Green", label: "Green", icon: "🟢" },
        { value: "Black", label: "Black", icon: "⚫" },
      ],
    },
  },
];

/* ---------------- main ---------------- */
export default function BookingFlow() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user, token } = useAuth();

  const countsFromState = state?.therapistCounts;
  const searchQueryFromState = state?.searchQuery; // Get search query from navigation state
  const selectedServiceIdFromState = state?.selectedServiceId; // Get pre-selected service ID from search
  const userLocationFromState = state?.userLocation; // Get user location from homepage

  // Extract referral code from URL query parameter or localStorage
  const [referralCode, setReferralCode] = useState(null);
  useEffect(() => {
    // First check URL for referral code
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get("ref");

    if (ref) {
      const code = ref.toUpperCase();
      console.log("🎟️ [BookingFlow] Referral code detected:", code);
      setReferralCode(code);
      // Save to localStorage so it persists through the booking flow
      localStorage.setItem("bookatherapy_referralCode", code);
    } else {
      // Check if we have a stored referral code from previous step
      const stored = localStorage.getItem("bookatherapy_referralCode");
      if (stored) {
        console.log("🎟️ [BookingFlow] Referral code loaded from storage");
        setReferralCode(stored);
      }
    }
  }, []);

  // Also try to get location from localStorage as fallback
  const [storedLocation, setStoredLocation] = useState(null);
  useEffect(() => {
    try {
      const stored = localStorage.getItem("bookatherapy_location");
      if (stored) {
        setStoredLocation(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Failed to parse stored location:", e);
    }
  }, []);

  // Load saved gender from localStorage
  const [savedGender, setSavedGender] = useState(null);
  useEffect(() => {
    try {
      const stored = localStorage.getItem("bookatherapy_clientGender");
      if (stored) {
        setSavedGender(stored);
      }
    } catch (e) {
      console.error("Failed to parse stored gender:", e);
    }
  }, []);

  // Temp user modal state - declare BEFORE useEffects that use them
  const [showTempUserModal, setShowTempUserModal] = useState(false);
  const [tempUserName, setTempUserName] = useState("");
  const [tempUserEmail, setTempUserEmail] = useState("");
  const [tempUserLoading, setTempUserLoading] = useState(false);
  const [tempUserError, setTempUserError] = useState("");
  const [tempUser, setTempUser] = useState(null);

  // Load temp user from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem("bookatherapy_tempUser");
      if (stored) {
        const parsed = JSON.parse(stored);
        setTempUser(parsed);
        setTempUserName(parsed.fullName || "");
        setTempUserEmail(parsed.email || "");
      }
    } catch (e) {
      console.error("Failed to parse stored temp user:", e);
    }
  }, []);

  // Guest users go straight through — name/email collected at Confirm step instead
  useEffect(() => {
    setShowTempUserModal(false);
  }, [user, tempUser]);

  // Handle temp user form submission
  const handleTempUserSubmit = async (e) => {
    e.preventDefault();
    if (!tempUserName.trim() || !tempUserEmail.trim()) {
      setTempUserError("Please enter both name and email");
      return;
    }

    if (!tempUserEmail.includes("@")) {
      setTempUserError("Please enter a valid email address");
      return;
    }

    try {
      setTempUserLoading(true);
      setTempUserError("");

      const response = await fetch(`${API}/auth/users/temp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: tempUserName.trim(),
          email: tempUserEmail.trim(),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to create temp user");
      }

      const data = await response.json();
      const userData = {
        _id: data.user._id,
        email: data.user.email,
        fullName: data.user.fullName,
        role: data.user.role,
      };

      // Save to localStorage
      localStorage.setItem("bookatherapy_tempUser", JSON.stringify(userData));
      setTempUser(userData);
      setShowTempUserModal(false);

      // Track Google Analytics event for temp user creation / booking flow start
      try {
        trackEvent("booking_flow_started", {
          user_id: userData._id,
          user_name: userData.fullName,
          // Don't include email in GA event for privacy
          flow_type: "temp_user",
          page_path: window.location.pathname,
        });

        // Also track as booking flow event
        trackBookingEvent("started", {
          user_id: userData._id,
          flow_type: "temp_user",
        });

        console.log("✅ Tracked booking flow start event in Google Analytics");
      } catch (trackingError) {
        console.warn(
          "Failed to track booking flow start event:",
          trackingError,
        );
        // Don't fail the user creation if tracking fails
      }
    } catch (error) {
      setTempUserError(
        error.message || "Failed to create temp user. Please try again.",
      );
    } finally {
      setTempUserLoading(false);
    }
  };

  const [answers, setAnswers] = useState({
    clientGender: null, // Client's gender for therapist filtering - auto-filled from localStorage
    comfortVibe: null,
    idealEscape: null,
    resetRitual: null,
    therapistEnergy: null,
    desiredOutcome: null,
    moodColor: null,
    dateISO: null,
    serviceId: null,
    duration: null,
    setting: [], // Changed to array for multi-select
    addOnIds: [],
    contactName: "",
    contactEmail: "",
  });
  const personaFieldKeys = useMemo(
    () => [
      "comfortVibe",
      "idealEscape",
      "resetRitual",
      "therapistEnergy",
      "desiredOutcome",
      "moodColor",
    ],
    [],
  );

  const [counts, setCounts] = useState(countsFromState || null);
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsError, setCountsError] = useState(null);

  const [savedPersona, setSavedPersona] = useState(null);
  const [personaLoading, setPersonaLoading] = useState(true);
  const [forcePersona, setForcePersona] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const suppressAutoAdvanceRef = useRef({});
  const autoSignatureRef = useRef({});

  async function getSavedPersonaOrNull() {
    try {
      const res = await fetch(`${API}/persona`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok)
        throw new Error(data?.message || "Failed to fetch saved persona");
      if (data && typeof data === "object") return data;
    } catch (_) {}
    return null;
  }

  // Load persona from localStorage when not authenticated, or from DB when authenticated
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setPersonaLoading(true);
        if (user) {
          // User is authenticated - load from DB
          const data = await getSavedPersonaOrNull();
          if (mounted) setSavedPersona(data?.persona);

          // Sync persona from localStorage to DB if it exists
          const localPersona = localStorage.getItem("bookatherapy_persona");
          if (localPersona) {
            try {
              const parsedPersona = JSON.parse(localPersona);
              if (personaIsFilled(parsedPersona)) {
                // Only sync if DB doesn't have a persona or if localStorage persona is more complete
                if (!data?.persona || !personaIsComplete(data.persona)) {
                  try {
                    await apiFetch(`${API}/persona/me`, {
                      method: "PUT",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                      },
                      credentials: "include",
                      body: JSON.stringify(parsedPersona),
                    });
                    console.log("✅ Synced persona from localStorage to DB");
                  } catch (err) {
                    console.error("Failed to sync persona to DB:", err);
                  }
                }
                // Clear localStorage persona after syncing
                localStorage.removeItem("bookatherapy_persona");
              }
            } catch (err) {
              console.error("Failed to parse localStorage persona:", err);
              localStorage.removeItem("bookatherapy_persona");
            }
          }

          // Check for pending booking and resume
          const pendingBooking = localStorage.getItem("pendingBooking");
          if (pendingBooking) {
            try {
              const { answers: savedAnswers, skipToTopRated } =
                JSON.parse(pendingBooking);
              setAnswers((prev) => ({ ...prev, ...savedAnswers }));
              localStorage.removeItem("pendingBooking");

              // Resume the booking flow by calling goMatches
              setTimeout(() => {
                goMatches(skipToTopRated).catch(() => {});
              }, 500);
            } catch (err) {
              console.error("Failed to restore pending booking:", err);
              localStorage.removeItem("pendingBooking");
            }
          }
        } else {
          // User is not authenticated - load from localStorage
          const localPersona = localStorage.getItem("bookatherapy_persona");
          if (localPersona) {
            try {
              const parsedPersona = JSON.parse(localPersona);
              if (personaIsFilled(parsedPersona)) {
                if (mounted) setSavedPersona(parsedPersona);
              }
            } catch (err) {
              console.error("Failed to parse localStorage persona:", err);
              localStorage.removeItem("bookatherapy_persona");
            }
          }
        }
      } finally {
        if (mounted) setPersonaLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user, token]);

  const hasSavedPersona = !!savedPersona && personaIsFilled(savedPersona);
  useEffect(() => {
    if (hasSavedPersona) {
      setAnswers((prev) => ({
        ...prev,
        comfortVibe: savedPersona.comfortVibe ?? prev.comfortVibe,
        idealEscape: savedPersona.idealEscape ?? prev.idealEscape,
        resetRitual: savedPersona.resetRitual ?? prev.resetRitual,
        therapistEnergy: savedPersona.therapistEnergy ?? prev.therapistEnergy,
        desiredOutcome: savedPersona.desiredOutcome ?? prev.desiredOutcome,
        moodColor: savedPersona.moodColor ?? prev.moodColor,
        contactName: user?.fullName ?? prev.contactName,
        contactEmail: user?.email ?? prev.contactEmail,
      }));
    }
    // eslint-disable-next-line
  }, [hasSavedPersona]);

  // Auto-fill gender from localStorage when loaded
  useEffect(() => {
    if (savedGender && !answers.clientGender) {
      setAnswers((prev) => ({
        ...prev,
        clientGender: savedGender,
      }));
    }
    // eslint-disable-next-line
  }, [savedGender]);

  // Save gender to localStorage when it changes
  useEffect(() => {
    if (answers.clientGender) {
      try {
        localStorage.setItem("bookatherapy_clientGender", answers.clientGender);
      } catch (e) {
        console.error("Failed to save gender to localStorage:", e);
      }
    }
  }, [answers.clientGender]);

  // Handle search query from home page - auto-select matching service
  useEffect(() => {
    // Priority 1: Use directly provided selectedServiceId from search
    let serviceIdToSet = selectedServiceIdFromState;

    // Priority 2: If no direct serviceId, try to match from search query
    if (!serviceIdToSet && searchQueryFromState) {
      serviceIdToSet = matchSearchQueryToService(searchQueryFromState);
    }

    if (serviceIdToSet) {
      // Store the service identifier (could be a name or ID)
      setAnswers((prev) => ({
        ...prev,
        serviceId: serviceIdToSet,
        _searchServiceIdentifier: serviceIdToSet, // Store original identifier for matching
      }));
      // Optionally skip to services step if persona is already filled
      if (hasSavedPersona && stepIndex === 0) {
        goToStep(1); // Go to services step
      }
    }
    // eslint-disable-next-line
  }, [searchQueryFromState, selectedServiceIdFromState]);

  // Fetch therapist counts when date, serviceId, or postal code changes
  useEffect(() => {
    if (!answers.dateISO) {
      setCounts(null);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setCountsLoading(true);
        setCountsError(null);
        // Extract serviceId from service object if it's an object
        const serviceIdForCounts =
          typeof answers.serviceId === "object"
            ? answers.serviceId?._id || answers.serviceId?.id
            : answers.serviceId;
        // Get client postal code from state or stored location
        const postalCodeForCounts =
          userLocationFromState?.postalCode ||
          storedLocation?.postalCode ||
          null;
        const data = await fetchTherapistCounts(
          answers.dateISO,
          serviceIdForCounts,
          postalCodeForCounts,
          answers.timeExact,
          answers.timePref,
          answers.clientGender,
        );
        if (mounted) {
          setCounts(data);
        }
      } catch (error) {
        if (mounted) {
          setCountsError(error.message || "Failed to load therapist counts");
          setCounts(null);
        }
      } finally {
        if (mounted) {
          setCountsLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [
    answers.dateISO,
    answers.serviceId,
    userLocationFromState?.postalCode,
    storedLocation?.postalCode,
  ]);

  /* Step model to match screenshot labels */
  const steps = useMemo(
    () => [
      { key: "persona", label: "Persona" },
      { key: "services", label: "Services" },
      { key: "details", label: "Details" },
    ],
    [],
  );
  const [stepIndex, setStepIndex] = useState(0);
  const active = steps[stepIndex].key;
  const isPersonaComplete = useMemo(
    () =>
      personaIsComplete({
        comfortVibe: answers.comfortVibe,
        idealEscape: answers.idealEscape,
        resetRitual: answers.resetRitual,
        therapistEnergy: answers.therapistEnergy,
        desiredOutcome: answers.desiredOutcome,
        moodColor: answers.moodColor,
      }),
    [
      answers.comfortVibe,
      answers.idealEscape,
      answers.resetRitual,
      answers.therapistEnergy,
      answers.desiredOutcome,
      answers.moodColor,
    ],
  );
  const hasSelectedService = useMemo(
    () => Boolean(answers.serviceId),
    [answers.serviceId],
  );
  const canProceedToMatches =
    Boolean(answers.dateISO) &&
    Array.isArray(answers.setting) &&
    answers.setting.length > 0;

  const goToStep = useCallback(
    (targetIndex, { suppressAutoAdvance = false } = {}) => {
      if (targetIndex < 0 || targetIndex >= steps.length) return;
      const targetKey = steps[targetIndex]?.key;
      if (!targetKey) return;

      if (suppressAutoAdvance) {
        suppressAutoAdvanceRef.current[targetKey] = true;
        if (targetKey === "persona") {
          setForcePersona(true);
        }
      }

      setStepIndex(targetIndex);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [steps, setForcePersona],
  );

  const setValue = useCallback(
    (key, value) => {
      // Handle multi-select for setting
      if (key === "setting") {
        setAnswers((prev) => {
          const currentSettings = Array.isArray(prev.setting)
            ? prev.setting
            : [];
          const newSettings = currentSettings.includes(value)
            ? currentSettings.filter((s) => s !== value) // Remove if already selected
            : [...currentSettings, value]; // Add if not selected
          return {
            ...prev,
            setting: newSettings,
            contactEmail: user?.email,
            contactName: user?.fullName,
          };
        });
      } else {
        setAnswers((prev) => ({
          ...prev,
          [key]: value,
          contactEmail: user?.email,
          contactName: user?.fullName,
        }));
      }

      const currentKey = steps[stepIndex]?.key;
      if (currentKey) {
        suppressAutoAdvanceRef.current[currentKey] = false;
      }

      if (forcePersona && personaFieldKeys.includes(key)) {
        setForcePersona(false);
      }
    },
    [
      user?.email,
      user?.fullName,
      stepIndex,
      steps,
      forcePersona,
      personaFieldKeys,
      setForcePersona,
    ],
  );

  async function goMatches(skipToTopRated = false) {
    // Allow viewing matches without authentication - removed the login requirement
    // Users can view therapists without logging in

    const persona = {
      comfortVibe: answers.comfortVibe,
      idealEscape: answers.idealEscape,
      resetRitual: answers.resetRitual,
      therapistEnergy: answers.therapistEnergy,
      desiredOutcome: answers.desiredOutcome,
      moodColor: answers.moodColor,
    };

    const hasPersona = personaIsFilled(persona);
    const useReviewBased = skipToTopRated || !hasPersona;

    // Save persona if user has created one
    if (hasPersona) {
      if (user && token) {
        // User is authenticated - save to DB
        if (!hasSavedPersona) {
          try {
            await apiFetch(`${API}/persona/me`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              credentials: "include",
              body: JSON.stringify(persona),
            });
          } catch (err) {
            console.error("Failed to save persona:", err);
          }
        }
      } else {
        // User is not authenticated - save to localStorage
        try {
          localStorage.setItem("bookatherapy_persona", JSON.stringify(persona));
          console.log("💾 Saved persona to localStorage");
        } catch (err) {
          console.error("Failed to save persona to localStorage:", err);
        }
      }
    }

    // answers.serviceId is now the full service object from backend
    const service = answers.serviceId;
    const commonContext = {
      dateISO: answers.dateISO || null,
      persona: hasPersona ? persona : null,
      service: service ? { id: service._id, name: service.name } : null,
      duration: answers.duration || null,
      addOns: answers.addOnIds || [],
      contact: {
        name: answers.contactName || "",
        email: answers.contactEmail || "",
      },
      matchingType: useReviewBased ? "review" : "persona",
    };

    // Get postal code - prefer homepage location, then localStorage, then user profile
    const clientPostalCode =
      userLocationFromState?.postalCode ||
      storedLocation?.postalCode ||
      user?.postalCode;

    console.log("🔍 Postal Code Sources:", {
      fromHomepage: userLocationFromState?.postalCode,
      fromLocalStorage: storedLocation?.postalCode,
      fromUserProfile: user?.postalCode,
      finalPostalCode: clientPostalCode,
    });

    // Multi-setting flow - fetch for selected settings
    const selectedSettings =
      Array.isArray(answers.setting) && answers.setting.length > 0
        ? answers.setting
        : ["Home", "Mobile", "Clinic"]; // Default to all if none selected

    // Extract serviceId from service object if it's an object
    const serviceIdForPreview =
      typeof answers.serviceId === "object"
        ? answers.serviceId?._id || answers.serviceId?.id
        : answers.serviceId;

    // Fetch matches for selected settings - use previewOne for both persona and non-persona cases
    // When persona is null/empty, previewOne will return therapists sorted by reviews/ratings
    const grouped = {};
    await Promise.all(
      selectedSettings.map(async (s) => {
        try {
          console.log(`🔍 Fetching therapists for setting: ${s}`, {
            hasPersona,
            dateISO: answers.dateISO,
            serviceId: serviceIdForPreview,
            clientGender: answers.clientGender,
            clientPostalCode,
          });

          const data = await previewOne({
            setting: s,
            dateISO: answers.dateISO || null,
            persona: hasPersona ? persona : null, // Pass null when no persona
            clientPostalCode, // Use postal code from homepage or user profile
            serviceId: serviceIdForPreview, // Pass serviceId to filter therapists
            clientGender: answers.clientGender, // Pass client gender for therapist filtering
          });

          console.log(`✅ Received data for setting ${s}:`, {
            hasResults: !!data?.results,
            resultsCount: data?.results?.length || 0,
            dataKeys: Object.keys(data || {}),
          });

          // Normalize: previewOne returns {results: [...]}, extract results
          const results = data?.results || data || [];
          grouped[s] = results;

          console.log(
            `📦 Stored ${results.length} therapists for setting ${s}`,
          );
        } catch (error) {
          console.error(
            `❌ Failed to fetch therapists for setting ${s}:`,
            error,
          );
          grouped[s] = [];
        }
      }),
    );

    console.log("📊 Final grouped data:", {
      settings: Object.keys(grouped),
      counts: Object.entries(grouped).map(([k, v]) => [
        k,
        Array.isArray(v) ? v.length : 0,
      ]),
    });
    navigate("/book/matches", {
      state: {
        multiSetting: true,
        grouped,
        context: { ...commonContext, setting: selectedSettings },
        referralCode, // Pass referral code through navigation
      },
    });
    return true;
  }

  async function next() {
    // persist persona each time you leave Persona
    if (active === "persona") {
      const persona = {
        comfortVibe: answers.comfortVibe,
        idealEscape: answers.idealEscape,
        resetRitual: answers.resetRitual,
        therapistEnergy: answers.therapistEnergy,
        desiredOutcome: answers.desiredOutcome,
        moodColor: answers.moodColor,
      };

      if (user && token) {
        // User is authenticated - save to DB
        try {
          await fetch(`${API}/persona`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            credentials: "include",
            body: JSON.stringify({
              ...persona,
              userEmail: answers.contactEmail,
              userName: answers.contactName,
            }),
          });
        } catch {}
      } else {
        // User is not authenticated - save to localStorage
        try {
          if (personaIsFilled(persona)) {
            localStorage.setItem(
              "bookatherapy_persona",
              JSON.stringify(persona),
            );
            console.log("💾 Saved persona to localStorage");
          }
        } catch (err) {
          console.error("Failed to save persona to localStorage:", err);
        }
      }
    }
    if (stepIndex < steps.length - 1) {
      const nextKey = steps[stepIndex + 1]?.key;
      if (nextKey) {
        suppressAutoAdvanceRef.current[nextKey] = false;
      }
      goToStep(stepIndex + 1);
    } else {
      await goMatches();
    }
  }
  useEffect(() => {
    const currentKey = steps[stepIndex]?.key;
    if (!currentKey) return;

    if (suppressAutoAdvanceRef.current[currentKey]) {
      return;
    }

    let ready = false;
    let signature = "";

    if (currentKey === "persona") {
      if (forcePersona) return;
      ready = personaIsComplete({
        comfortVibe: answers.comfortVibe,
        idealEscape: answers.idealEscape,
        resetRitual: answers.resetRitual,
        therapistEnergy: answers.therapistEnergy,
        desiredOutcome: answers.desiredOutcome,
        moodColor: answers.moodColor,
      });
      signature = JSON.stringify({
        comfortVibe: answers.comfortVibe,
        idealEscape: answers.idealEscape,
        resetRitual: answers.resetRitual,
        therapistEnergy: answers.therapistEnergy,
        desiredOutcome: answers.desiredOutcome,
        moodColor: answers.moodColor,
      });
    } else if (currentKey === "services") {
      ready = Boolean(answers.serviceId && answers.duration);
      const serviceIdStr =
        typeof answers.serviceId === "object"
          ? answers.serviceId?._id || ""
          : answers.serviceId || "";
      signature = `${serviceIdStr}|${answers.duration?.duration || ""}|${
        (answers.addOnIds || []).length
      }`;
    } else if (currentKey === "details") {
      // Require both date and at least one setting before auto-advancing
      ready = Boolean(
        answers.dateISO && answers.setting && answers.setting.length > 0,
      );
      signature = `${answers.dateISO || ""}|${
        Array.isArray(answers.setting) ? answers.setting.join(",") : ""
      }`;
    }

    if (!ready) {
      return;
    }

    if (currentKey === "details") {
      autoSignatureRef.current[currentKey] = signature;
      return;
    }

    if (autoSignatureRef.current[currentKey] === signature) {
      return;
    }

    const delay = currentKey === "details" ? 900 : 500;
    const timer = setTimeout(() => {
      autoSignatureRef.current[currentKey] = signature;
      next().catch(() => {});
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [answers, forcePersona, stepIndex, steps]);

  return (
    <div className="min-h-screen flex items-center justify-center p-2 sm:p-4 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 relative overflow-hidden">
      {/* Temp User Modal - Shows for unauthenticated users */}
      {showTempUserModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Let's get started
              </h2>

              <p className="text-gray-600 mb-6">
                Please provide your name and email to continue with your booking
              </p>
            </div>

            {tempUserError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {tempUserError}
              </div>
            )}

            <form onSubmit={handleTempUserSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter your full name"
                  value={tempUserName}
                  onChange={(e) => setTempUserName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                  disabled={tempUserLoading}
                  autoFocus
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={tempUserEmail}
                  onChange={(e) => setTempUserEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                  disabled={tempUserLoading}
                  required
                />
              </div>

              <div className="flex flex-col gap-3 pt-2">
                <button
                  type="submit"
                  disabled={tempUserLoading}
                  className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold hover:from-emerald-700 hover:to-teal-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
                  {tempUserLoading ? "Creating..." : "Continue"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Background decorative elements */}

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-200/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-teal-200/30 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-5xl max-h-[96vh] sm:max-h-[90vh] flex flex-col rounded-2xl sm:rounded-3xl bg-white/90 backdrop-blur-xl shadow-2xl relative z-10 overflow-hidden">
        {/* top stepper like screenshot: Persona | Services | Details - Sticky at top */}
        <div className="flex-shrink-0 px-3 sm:px-6 pt-4 sm:pt-6">
          <StepperHeader steps={steps} active={stepIndex} />
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-6 pb-6">
          {/* persona banner */}
          {personaLoading ? (
            <div className="mb-3 text-xs text-gray-500">
              Loading your preferences…
            </div>
          ) : (
            hasSavedPersona &&
            !forcePersona && (
              <div className="mb-4 text-xs sm:text-sm flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-emerald-900">
                <span>Using your saved preferences for matching.</span>
                <button
                  type="button"
                  onClick={() => {
                    setForcePersona(true);
                    goToStep(0);
                  }}
                  className="ml-auto underline underline-offset-2 hover:opacity-80">
                  Edit preferences
                </button>
              </div>
            )
          )}

          {/* --- PERSONA (all three pairs on one screen as cards) --- */}
          {active === "persona" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="space-y-5">
              {/* Option to skip persona */}
              <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      🎯 Get personalized matches
                    </h3>
                    <p className="text-sm text-gray-700">
                      Answer these quick questions to find therapists that match
                      your vibe, or{" "}
                      <button
                        type="button"
                        onClick={() => {
                          // Clear persona and proceed to next step
                          setAnswers((prev) => ({
                            ...prev,
                            comfortVibe: null,
                            idealEscape: null,
                            resetRitual: null,
                            therapistEnergy: null,
                            desiredOutcome: null,
                            moodColor: null,
                          }));
                          goToStep(1); // Skip to services
                        }}
                        className="text-emerald-700 font-semibold hover:underline focus:outline-none">
                        skip to see top-rated therapists
                      </button>
                    </p>
                  </div>
                </div>
              </div>

              {personaPairs.map((row, idx) => (
                <motion.div
                  key={row.left.qkey}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[row.left, row.right].map((col) => (
                    <div
                      key={col.qkey}
                      className="rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-sm p-4 hover:shadow-md transition-shadow">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {col.title}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {col.options.map((opt) => (
                          <CardOption
                            key={opt.value}
                            icon={opt.icon}
                            label={opt.label}
                            selected={answers[col.qkey] === opt.value}
                            onClick={() => setValue(col.qkey, opt.value)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </motion.div>
              ))}

              {/* Skip button */}
              <div className="flex justify-center pt-4">
                <button
                  type="button"
                  onClick={() => {
                    // Clear persona and proceed to next step
                    setAnswers((prev) => ({
                      ...prev,
                      comfortVibe: null,
                      idealEscape: null,
                      resetRitual: null,
                      therapistEnergy: null,
                      desiredOutcome: null,
                      moodColor: null,
                    }));
                    goToStep(1); // Skip to services
                  }}
                  className="px-8 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400">
                  Skip to top-rated therapists
                </button>
              </div>
            </motion.div>
          )}

          {/* --- SERVICES (same component) --- */}
          {active === "services" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}>
              <ServicePickerPremium
                selectedServiceId={answers.serviceId}
                selectedDuration={answers.duration}
                selectedAddOns={answers.addOnIds}
                onServiceSelect={(service) => setValue("serviceId", service)}
                onDurationSelect={(duration) => setValue("duration", duration)}
                onAddOnsChange={(addOns) => setValue("addOnIds", addOns)}
              />
            </motion.div>
          )}

          {/* --- DETAILS (date + setting like screenshot section tiles) --- */}
          {active === "details" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="space-y-5">
              <div className="rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-sm p-4 hover:shadow-md transition-shadow">
                <div className="text-sm font-semibold text-gray-900 mb-2">
                  Pick a date
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Chip
                    active={answers.dateISO === todayStr()}
                    onClick={() => setValue("dateISO", todayStr())}>
                    Today
                  </Chip>
                  <Chip
                    active={answers.dateISO === tomorrowStr()}
                    onClick={() => setValue("dateISO", tomorrowStr())}>
                    Tomorrow
                  </Chip>
                  <Chip
                    active={answers.dateISO === nextWeekendStr()}
                    onClick={() => setValue("dateISO", nextWeekendStr())}>
                    This weekend
                  </Chip>
                </div>
                <input
                  type="date"
                  min={todayStr()}
                  value={answers.dateISO || ""}
                  onChange={(e) => setValue("dateISO", e.target.value || null)}
                  className="h-11 w-full rounded-xl border border-gray-300 px-3 text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                />
                {!answers.dateISO && (
                  <div className="mt-2 text-xs text-red-600">
                    Please select a date.
                  </div>
                )}
              </div>

              {/* Gender Selection */}
              <div className="rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-sm p-4 hover:shadow-md transition-shadow">
                <div className="text-sm font-semibold text-gray-900 mb-2">
                  Your Gender
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  Some therapists have gender preferences for their clients.
                  This helps us show you available therapists.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { value: "male", label: "Male", icon: "👨" },
                    { value: "female", label: "Female", icon: "👩" },
                    {
                      value: "other",
                      label: "Other/Prefer not to say",
                      icon: "🧑",
                    },
                  ].map((option) => (
                    <CardOption
                      key={option.value}
                      icon={option.icon}
                      label={option.label}
                      selected={answers.clientGender === option.value}
                      onClick={() => setValue("clientGender", option.value)}
                    />
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-sm p-4 hover:shadow-md transition-shadow">
                <div className="text-sm font-semibold text-gray-900 mb-3">
                  Select a setting
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  Choose where you'd like your massage. You can select more than
                  one option to see all available therapists.
                </p>
                {countsLoading && (
                  <div className="mb-3 text-sm text-gray-500">
                    Loading therapist availability…
                  </div>
                )}
                {countsError && (
                  <div className="mb-3 text-sm text-red-600">{countsError}</div>
                )}
                <TooltipProvider>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      {
                        value: "Home",
                        label: "Home-based",
                        icon: "🏠",
                        tooltip:
                          "Therapist comes to your home. You provide the space and setup.",
                      },
                      {
                        value: "Mobile",
                        label: "Mobile",
                        icon: "🚗",
                        tooltip:
                          "Therapist brings a portable table to your location (home, office, etc.).",
                      },
                      {
                        value: "Clinic",
                        label: "Clinic-based",
                        icon: "🏥",
                        tooltip:
                          "Visit the therapist at their professional clinic or spa location.",
                      },
                    ].map((opt) => (
                      <Tooltip key={opt.value}>
                        <TooltipTrigger asChild>
                          <div>
                            <CardOption
                              icon={opt.icon}
                              label={opt.label}
                              selected={
                                Array.isArray(answers.setting) &&
                                answers.setting.includes(opt.value)
                              }
                              count={counts?.[opt.value]}
                              onClick={() => setValue("setting", opt.value)}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">{opt.tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </TooltipProvider>
                {(!answers.setting || answers.setting.length === 0) && (
                  <div className="mt-2 text-xs text-red-600">
                    Please select at least one setting to continue.
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* footer */}
          <div className="mt-8 flex flex-col gap-1 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Review your details and click Proceed when you're ready.
            </span>
          </div>

          <div className="mt-4 text-xs text-gray-500 text-right">
            These answers help us match you with the therapist who feels like
            home ✨
          </div>
        </div>

        {/* Sticky Footer with Back and Next/Proceed Button */}
        <div className="flex-shrink-0 sticky bottom-0 z-20 bg-white/95 backdrop-blur-sm border-t border-gray-200 px-3 sm:px-6 py-3 sm:py-4 shadow-lg safe-bottom">
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            {/* Back Button - Only show if not on first step */}
            {stepIndex > 0 && (
              <button
                type="button"
                onClick={() => goToStep(stepIndex - 1)}
                className="px-6 py-3 rounded-xl font-semibold text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400">
                Back
              </button>
            )}

            {/* Spacer to push Next button to the right when Back button is present */}
            {stepIndex === 0 && <div />}

            {/* Next/Proceed Button */}
            <div className="flex justify-end">
              {active === "persona" && (
                <button
                  type="button"
                  onClick={() => next()}
                  disabled={!isPersonaComplete}
                  className={clsx(
                    "px-8 py-3 rounded-xl font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
                    isPersonaComplete
                      ? "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500"
                      : "bg-emerald-300 cursor-not-allowed opacity-70",
                  )}>
                  Next
                </button>
              )}
              {active === "services" && (
                <button
                  type="button"
                  onClick={() => next()}
                  disabled={!hasSelectedService}
                  className={clsx(
                    "px-8 py-3 rounded-xl font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
                    hasSelectedService
                      ? "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500"
                      : "bg-emerald-300 cursor-not-allowed opacity-70",
                  )}>
                  Next
                </button>
              )}
              {active === "details" && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!canProceedToMatches || isLoadingMatches) return;
                    setIsLoadingMatches(true);
                    try {
                      const navigated = await goMatches();
                      if (!navigated) {
                        setIsLoadingMatches(false);
                      }
                    } catch (err) {
                      console.error("Failed to load matches:", err);
                      setIsLoadingMatches(false);
                    }
                  }}
                  disabled={!canProceedToMatches || isLoadingMatches}
                  className={clsx(
                    "px-6 py-3 rounded-xl font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2",
                    canProceedToMatches && !isLoadingMatches
                      ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md hover:shadow-lg hover:from-emerald-700 hover:to-teal-700"
                      : "bg-gray-200 text-gray-500 cursor-not-allowed",
                  )}>
                  {isLoadingMatches ? "Brewing Matches…" : "Proceed"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isLoadingMatches && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}>
            <div className="flex flex-col items-center gap-6 text-center">
              <div className="w-64 h-64">
                <Lottie
                  animationData={brewingCoffeeAnimation}
                  loop
                  autoplay
                  className="w-full h-full"
                />
              </div>
              <div className="space-y-2">
                <p className="text-2xl font-semibold text-gray-900">
                  Brewing your perfect matches ☕
                </p>
                <p className="text-sm text-gray-600 max-w-md">
                  Sit back while we pour through your preferences and find
                  therapists who fit just right.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Sign in to Continue
              </h2>

              <p className="text-gray-600 mb-6">
                Please log in or create an account to view therapist matches and
                complete your booking
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    navigate("/auth", { state: { returnTo: "/book" } });
                  }}
                  className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold hover:from-emerald-700 hover:to-teal-700 transition-all shadow-md hover:shadow-lg">
                  Sign In / Create Account
                </button>

                <button
                  onClick={() => setShowLoginPrompt(false)}
                  className="w-full px-6 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
